import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import founderImg from '@/assets/founder.webp';

// Apple-inspired easing
const appleEase: [number, number, number, number] = [0.22, 1, 0.36, 1];
const duration = 0.8;
const stagger = 0.1;
export default function SocialProofSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: false,
    margin: '-80px'
  });
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: stagger
      }
    }
  };
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 8
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: duration * 0.6,
        ease: appleEase
      }
    }
  };
  const slideUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration,
        ease: appleEase
      }
    }
  };
  const lineReveal = {
    hidden: {
      scaleX: 0,
      originX: 0
    },
    visible: {
      scaleX: 1,
      transition: {
        duration: duration * 0.7,
        ease: appleEase
      }
    }
  };
  return <section ref={ref} className="py-20 md:py-28 bg-muted overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* Photo with premium hover effect */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={isInView ? {
            opacity: 1,
            scale: 1
          } : {
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration,
            ease: appleEase
          }} whileHover={{
            scale: 1.03,
            transition: {
              duration: 0.4,
              ease: appleEase
            }
          }} className="relative flex-shrink-0 group cursor-pointer">
              <div className="w-64 md:w-80 aspect-[4/3] rounded-2xl overflow-hidden bg-muted ring-4 ring-accent/30 shadow-xl transition-shadow duration-500 group-hover:shadow-2xl">
                <img src={founderImg} alt="Gilbert Loane, Founder of UnlockEd Tutoring, speaking on a panel" width={1834} height={1269} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              {/* Animated accent glow */}
              <motion.div className="absolute -bottom-2 -right-2 w-12 h-12 bg-accent/40 rounded-full blur-xl" animate={{
              scale: [1, 1.2, 1],
              opacity: [0.4, 0.6, 0.4]
            }} transition={{
              duration: 3,
              ease: "easeInOut",
              repeat: Infinity
            }} />
            </motion.div>

            {/* Content */}
            <motion.div variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="flex flex-col text-center md:text-left">
              <motion.p variants={fadeIn} className="text-sm md:text-base font-semibold tracking-[0.2em] uppercase text-accent mb-3">
                Meet the Founder
              </motion.p>

              <motion.h2 variants={slideUp} className="font-display text-xl md:text-2xl font-bold tracking-tight leading-snug mb-3">
                Built by a Tasmanian, for Tasmanians
              </motion.h2>

              <motion.div variants={lineReveal} className="h-[2px] w-10 bg-gradient-to-r from-foreground/30 to-transparent mb-4 mx-auto md:mx-0" />

              <motion.p variants={slideUp} className="text-sm md:text-base text-muted-foreground leading-[1.7] max-w-lg">"Growing up in a small town in Tasmania's Fingal Valley and completing my schooling in Launceston, I had no idea what opportunities were out there waiting for me. That's why I built UnlockEd Tutoring — to help students discover their potential, one step at a time, and to build confidence for that next step."</motion.p>

              <motion.div variants={slideUp} className="mt-4">
                <p className="font-semibold text-foreground text-sm">Gilbert Loane</p>
                <p className="text-xs text-muted-foreground">Founder, UnlockEd Tutoring</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}